import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Trash2,
  AlertCircle,
  Clock,
  ArrowUpDown,
  Download,
  CreditCard,
  ClipboardList,
  CheckSquare,
  Loader2,
  Building2,
} from "lucide-react";
import { useAuth } from "@/components/auth-provider";
import { MaintenanceRequestDialog } from "@/components/maintenance/maintenance-request-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";

// Import Firebase services and types
import {
  getMaintenanceRequests,
  updateMaintenanceStatus,
  deleteMaintenanceRequest,
  getMaintenanceStats,
} from "@/lib/maintenance-service";
import { getProperties } from "@/lib/property-service";
import { getLandlordLeases } from "@/lib/lease-service";
import { MaintenanceStatusBadge, PriorityBadge } from "@/lib/maintenance-utils";
import { doc, getDoc } from "firebase/firestore";
import { getFirebaseDB } from "@/lib/firebase";
import { MaintenanceRequest, Property, MaintenanceStatus } from "@/types/types";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

// Define Zod schemas
const statusUpdateFormSchema = z.object({
  status: z.string().min(1, "Please select a status"),
  scheduledDate: z.string().optional(),
  assignedTo: z.string().max(200, "Name too long").optional(),
  assignedPhone: z
    .string()
    .regex(/^[\d\s\-\+\(\)]*$/, "Invalid phone format")
    .optional(),
  actualCost: z.number().min(0, "Cost cannot be negative").optional(),
  completedDate: z.string().optional(),
  notes: z.string().max(1000, "Notes too long").optional(),
});

type StatusUpdateFormData = z.infer<typeof statusUpdateFormSchema>;

interface TenantLease {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantEmail: string;
  propertyId: string;
  unitId?: string;
  unitName?: string;
}

export default function MaintenancePage() {
  const { user } = useAuth();

  // State for data
  const [requests, setRequests] = useState<MaintenanceRequest[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [tenantLeases, setTenantLeases] = useState<TenantLease[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stats, setStats] = useState<any>(null);

  // State for filters and search
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("dateSubmitted");
  const [sortOrder, setSortOrder] = useState("desc");

  // State for dialogs and modals
  const [selectedRequest, setSelectedRequest] =
    useState<MaintenanceRequest | null>(null);
  const [isAddRequestOpen, setIsAddRequestOpen] = useState(false);
  const [isStatusUpdateOpen, setIsStatusUpdateOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] =
    useState<MaintenanceRequest | null>(null);

  // Form instances
  const statusForm = useForm<StatusUpdateFormData>({
    resolver: zodResolver(statusUpdateFormSchema),
    defaultValues: {
      status: "",
      scheduledDate: "",
      assignedTo: "",
      assignedPhone: "",
      actualCost: 0,
      completedDate: "",
      notes: "",
    },
  });

  // Load initial data
  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const db = getFirebaseDB();
      if (!db) throw new Error("Firebase not initialized");

      const [requestsData, propertiesResult, leasesResult, statsData] =
        await Promise.all([
          getMaintenanceRequests({
            userId: user.id,
            userRole: "landlord",
          }),
          getProperties(user.id, 100),
          getLandlordLeases(user.id, 100),
          getMaintenanceStats({
            userId: user.id,
            userRole: "landlord",
          }),
        ]);

      // Filter for active leases and build tenant lease data
      const activeLeases = leasesResult.data.filter(
        (lease) => lease.status === "active"
      );
      const tenantLeasesData: TenantLease[] = await Promise.all(
        activeLeases.map(async (lease) => {
          // Get tenant user data
          const tenantRef = doc(db, "users", lease.tenantId);
          const tenantSnap = await getDoc(tenantRef);
          const tenantUser = tenantSnap.exists() ? tenantSnap.data() : null;

          // Find property for unit info
          const property = propertiesResult.data.find(
            (p) => p.id === lease.propertyId
          );
          let unitName = "Main Unit";

          if (lease.unitId && property?.units) {
            const unit = property.units.find((u) => u.id === lease.unitId);
            unitName = unit?.name || lease.unitId;
          }

          return {
            id: lease.id,
            tenantId: lease.tenantId,
            tenantName:
              tenantUser?.name || tenantUser?.displayName || "Unknown Tenant",
            tenantEmail: tenantUser?.email || "",
            propertyId: lease.propertyId,
            unitId: lease.unitId,
            unitName,
          };
        })
      );

      setRequests(requestsData);
      setProperties(propertiesResult.data);
      setTenantLeases(tenantLeasesData);
      setStats(statsData);
    } catch (error) {
      console.error("Error fetching maintenance data:", error);
      toast.error("Failed to load maintenance data");
    } finally {
      setIsLoading(false);
    }
  };

  // Enrich requests with property images
  const enrichedRequests = requests.map((request) => {
    const property = properties.find((p) => p.id === request.propertyId);
    return {
      ...request,
      propertyImage: property?.image || null,
    };
  });

  // Filter and sort requests
  const filteredRequests = enrichedRequests
    .filter((request) => {
      const matchesSearch =
        searchTerm === "" ||
        request.propertyTitle
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        request.tenantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || request.status === statusFilter;
      const matchesProperty =
        propertyFilter === "all" || request.propertyId === propertyFilter;
      const matchesCategory =
        categoryFilter === "all" || request.category === categoryFilter;
      const matchesPriority =
        priorityFilter === "all" || request.priority === priorityFilter;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesProperty &&
        matchesCategory &&
        matchesPriority
      );
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case "title":
          aValue = a.title;
          bValue = b.title;
          break;
        case "property":
          aValue = a.propertyTitle;
          bValue = b.propertyTitle;
          break;
        case "tenant":
          aValue = a.tenantName;
          bValue = b.tenantName;
          break;
        case "dateSubmitted":
          aValue = new Date(a.dateSubmitted);
          bValue = new Date(b.dateSubmitted);
          break;
        case "priority":
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority as keyof typeof priorityOrder];
          bValue = priorityOrder[b.priority as keyof typeof priorityOrder];
          break;
        case "status":
          const statusOrder = {
            pending: 4,
            scheduled: 3,
            "in-progress": 2,
            completed: 1,
            cancelled: 0,
          };
          aValue = statusOrder[a.status as keyof typeof statusOrder];
          bValue = statusOrder[b.status as keyof typeof statusOrder];
          break;
        default:
          return 0;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

  // Helper functions
  const formatNaira = (amount: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getDaysSinceSubmission = (dateStr: string) => {
    const today = new Date();
    const submitted = new Date(dateStr);
    const diffTime = today.getTime() - submitted.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const formatCategory = (category: string) => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Event handlers
  const handleStatusUpdate = async (data: StatusUpdateFormData) => {
    if (!selectedRequest) return;

    setIsSubmitting(true);
    try {
      await updateMaintenanceStatus(
        selectedRequest.id,
        data.status as MaintenanceStatus,
        {
          scheduledDate: data.scheduledDate || undefined,
          assignedTo: data.assignedTo || undefined,
          assignedPhone: data.assignedPhone || undefined,
          actualCost: data.actualCost || undefined,
          completedDate: data.completedDate || undefined,
          notes: data.notes || undefined,
        }
      );

      toast.success("Maintenance status updated successfully");
      setIsStatusUpdateOpen(false);
      statusForm.reset();

      // Refresh data
      await fetchData();
    } catch (error) {
      console.error("Error updating maintenance status:", error);
      toast.error("Failed to update maintenance status");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!requestToDelete) return;

    setIsSubmitting(true);
    try {
      await deleteMaintenanceRequest(requestToDelete.id);

      toast.success("Maintenance request deleted successfully");
      setIsDeleteDialogOpen(false);
      setRequestToDelete(null);

      // Refresh data
      await fetchData();
    } catch (error) {
      console.error("Error deleting maintenance request:", error);
      toast.error("Failed to delete maintenance request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openStatusUpdateDialog = (request: MaintenanceRequest) => {
    setSelectedRequest(request);
    statusForm.reset({
      status: request.status,
      scheduledDate: request.scheduledDate || "",
      assignedTo: request.assignedTo || "",
      assignedPhone: request.assignedPhone || "",
      actualCost: request.actualCost || 0,
      completedDate: request.completedDate || "",
      notes: request.notes || "",
    });
    setIsStatusUpdateOpen(true);
  };

  const confirmDeleteRequest = (request: MaintenanceRequest) => {
    setRequestToDelete(request);
    setIsDeleteDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Requests
            </CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.pending || 0} pending, {stats?.inProgress || 0} in
              progress, {stats?.completed || 0} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Priority</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.highPriority || 0}</div>
            <p className="text-xs text-muted-foreground">
              Unresolved high issues
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Maintenance Cost
            </CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNaira(stats?.totalCost || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Total spent on maintenance
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Avg. Resolution Time
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.averageResolutionTime?.toFixed(1) || 0} days
            </div>
            <p className="text-xs text-muted-foreground">
              From submission to completion
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-[300px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search maintenance requests..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select value={propertyFilter} onValueChange={setPropertyFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map((property) => (
                  <SelectItem key={property.id} value={property.id}>
                    {property.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="plumbing">Plumbing</SelectItem>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="hvac">HVAC</SelectItem>
                <SelectItem value="appliance">Appliance</SelectItem>
                <SelectItem value="structural">Structural</SelectItem>
                <SelectItem value="pest">Pest Control</SelectItem>
                <SelectItem value="landscaping">Landscaping</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <Button variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>

          <Button size="sm" onClick={() => setIsAddRequestOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Request
          </Button>
        </div>
      </div>

      {/* Maintenance Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>Maintenance Requests</CardTitle>
          <CardDescription>
            Manage and track all maintenance requests for your properties.
            Showing {filteredRequests.length} of {requests.length} requests.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">Issue</TableHead>
                <TableHead>Property</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSortBy("dateSubmitted");
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    }}
                  >
                    Date{" "}
                    {sortBy === "dateSubmitted" && (
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    )}
                  </Button>
                </TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No maintenance requests found matching your filters.
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <div className="font-medium">{request.title}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                          {formatCategory(request.category)} -{" "}
                          {request.unitName}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-md overflow-hidden relative bg-muted flex items-center justify-center">
                          {request.propertyImage ? (
                            <img
                              src={request.propertyImage}
                              alt={request.propertyTitle}
                              width="32"
                              height="32"
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                        <div className="text-sm truncate max-w-[120px]">
                          {request.propertyTitle}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>
                          {new Date(request.dateSubmitted).toLocaleDateString()}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {getDaysSinceSubmission(request.dateSubmitted)} days
                          ago
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <PriorityBadge priority={request.priority} />
                    </TableCell>
                    <TableCell>
                      <MaintenanceStatusBadge status={request.status} />
                    </TableCell>
                    <TableCell>
                      {request.assignedTo ? (
                        <div className="flex flex-col">
                          <span className="text-sm">{request.assignedTo}</span>
                          {request.scheduledDate &&
                            request.status !== "completed" && (
                              <span className="text-xs text-muted-foreground">
                                {request.scheduledDate}
                              </span>
                            )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          Not assigned
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openStatusUpdateDialog(request)}
                        >
                          <CheckSquare className="h-4 w-4 mr-1" />
                          Update
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => confirmDeleteRequest(request)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Request Dialog */}
      <MaintenanceRequestDialog
        open={isAddRequestOpen}
        onOpenChange={setIsAddRequestOpen}
        onSuccess={() => setIsAddRequestOpen(false)}
        properties={properties}
        tenantLeases={tenantLeases}
        userId={user?.id || ""}
        mode="landlord"
      />

      {/* Status Update Dialog */}
      <Dialog open={isStatusUpdateOpen} onOpenChange={setIsStatusUpdateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-2xl font-semibold">
              Update Status
            </DialogTitle>
            <DialogDescription className="text-base">
              Update the progress and details of this maintenance request.
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="bg-muted/50 p-4 rounded-lg border space-y-1">
              <h3 className="font-semibold text-lg">{selectedRequest.title}</h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="h-4 w-4" />
                <span>{selectedRequest.propertyTitle}</span>
                <span>•</span>
                <span>{selectedRequest.unitName}</span>
              </div>
            </div>
          )}

          <Form {...statusForm}>
            <form
              onSubmit={statusForm.handleSubmit(handleStatusUpdate)}
              className="flex flex-col overflow-hidden flex-1"
            >
              <div className="overflow-y-auto px-1 space-y-8 flex-1 py-2">
                {/* Status Section */}
                <FormField
                  control={statusForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">Status *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="pending">
                            <div className="flex items-center gap-2">
                              <MaintenanceStatusBadge status="pending" />
                              <span className="text-sm">Awaiting action</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="scheduled">
                            <div className="flex items-center gap-2">
                              <MaintenanceStatusBadge status="scheduled" />
                              <span className="text-sm">Appointment set</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="in-progress">
                            <div className="flex items-center gap-2">
                              <MaintenanceStatusBadge status="in-progress" />
                              <span className="text-sm">Work underway</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="completed">
                            <div className="flex items-center gap-2">
                              <MaintenanceStatusBadge status="completed" />
                              <span className="text-sm">Issue resolved</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="cancelled">
                            <div className="flex items-center gap-2">
                              <MaintenanceStatusBadge status="cancelled" />
                              <span className="text-sm">No longer needed</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Assignment Section */}
                <div className="space-y-6 pt-6 border-t">
                  <div className="text-sm font-medium text-muted-foreground">
                    Assignment Details
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={statusForm.control}
                      name="assignedTo"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base">
                            Assigned To
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Contractor or service provider"
                              className="h-11"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={statusForm.control}
                      name="assignedPhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base">
                            Contact Number
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="+234 xxx xxx xxxx"
                              className="h-11"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Schedule & Cost Section */}
                <div className="space-y-6 pt-6 border-t">
                  <div className="text-sm font-medium text-muted-foreground">
                    Schedule & Budget
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={statusForm.control}
                      name="scheduledDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base">
                            Scheduled Date
                          </FormLabel>
                          <FormControl>
                            <Input type="date" className="h-11" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={statusForm.control}
                      name="actualCost"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base">
                            Actual Cost
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                ₦
                              </span>
                              <Input
                                type="number"
                                min="0"
                                step="1000"
                                placeholder="0"
                                className="h-11 pl-8"
                                {...field}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value
                                      ? Number(e.target.value)
                                      : undefined
                                  )
                                }
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Notes Section */}
                <div className="space-y-6 pt-6 border-t">
                  <FormField
                    control={statusForm.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base">
                          Status Notes
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Add any updates, progress notes, or additional information..."
                            className="min-h-[120px] resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <DialogFooter className="mt-6 pt-6 border-t gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsStatusUpdateOpen(false)}
                  disabled={isSubmitting}
                  className="h-11"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-11 min-w-[120px]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Status"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-xl font-semibold">
              Delete Maintenance Request?
            </DialogTitle>
            <DialogDescription className="text-base">
              This will permanently delete the maintenance request. This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {requestToDelete && (
            <div className="bg-muted/50 p-4 rounded-lg border">
              <p className="font-medium">{requestToDelete.title}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {requestToDelete.propertyTitle} - {requestToDelete.unitName}
              </p>
            </div>
          )}

          <DialogFooter className="gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isSubmitting}
              className="h-11"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteRequest}
              disabled={isSubmitting}
              className="h-11"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Request"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
